import { yupResolver } from "@hookform/resolvers/yup";
import {
  TipoSituacaoProcessoJudicial,
  getipoSitucoesProcessoJudicalValue,
} from "constants/EnumTipoSituacaoProcessoJudicial";
import { api } from "constants/RotasAPI";
import { usePostCriarProcessoJudicialHistorico } from "hooks/processosJudiciais/usePostCriarProcessoJudicialHistorico";
import { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import CustomButton from "shared/components/button/CustomButton";
import { CheckBox } from "shared/components/checkbox/CheckBox";
import { CustomDatePicker } from "shared/components/date/CustomDatePicker";
import CustomTextArea from "shared/components/input/CustomTextArea";
import CustomModal from "shared/components/modal/CustomModal";
import CustomMultiSelectAsync from "shared/components/select/CustomMultiSelectAsync";
import CustomSelect from "shared/components/select/CustomSelect";
import showAlert from "shared/util/functions/alert/showAlert";
import { modalAdicionarProcessoJudicialHistoricoSchema } from "shared/yup/schemas/ModalAdicionarHistoricoProcessoJudicialSchema";
import { RegistroAcordoDTO } from "types/dto/RegistroAcordoDTO";
import { RequestCriarProcessoJudicialHistorico } from "types/request/RequestCriarProcessoJudicialHistorico";
import { SelectOptionType } from "types/select/SelectOptionType";

interface ModalAdicionarProcessoJudicialHistoricoProps {
  showModal: boolean;
  onCloseModal: () => void;
  unidadeId?: number | null;
  processoId: number | null | undefined;
}

interface ModalAdicionarHistoricoProcessoJudicialForm {
  data: Date;
  situacao: TipoSituacaoProcessoJudicial | undefined | null;
  anotacoes: string | undefined;
  possuiAcordosVinculados: boolean;
  acordos: (SelectOptionType<RegistroAcordoDTO> | undefined)[] | undefined;
}

const ModalAdicionarProcessoJudicialHistorico: React.FC<ModalAdicionarProcessoJudicialHistoricoProps> = (props) => {
  const { register, handleSubmit, reset, formState, control, setValue, clearErrors, watch } =
    useForm<ModalAdicionarHistoricoProcessoJudicialForm>({
      resolver: yupResolver<ModalAdicionarHistoricoProcessoJudicialForm>(modalAdicionarProcessoJudicialHistoricoSchema),
    });

  const [cadastrarOutroHistorico, setCadastrarOutroHistorico] = useState<boolean>(false);
  const [possuiAcordosVinculados, setPossuiAcordosVinculados] = useState<boolean>(false);
  const [toogleAtributoRefresh, setToogleAtributoRefresh] = useState<boolean>(false);

  const opcoesSitucoesProcessoJudical: SelectOptionType<TipoSituacaoProcessoJudicial>[] = [
    {
      value: TipoSituacaoProcessoJudicial.ACAO_DE_COBRANCA,
      label: "Ação de cobrança",
    },
    {
      value: TipoSituacaoProcessoJudicial.ACAO_DE_EXECUCAO,
      label: "Ação de execução",
    },
    {
      value: TipoSituacaoProcessoJudicial.AJUIZADO,
      label: "Ajuizado",
    },
    {
      value: TipoSituacaoProcessoJudicial.AR_ENVIADO,
      label: "A.R enviado",
    },
    {
      value: TipoSituacaoProcessoJudicial.AR_POSITIVO,
      label: "A.R positivo",
    },
    {
      value: TipoSituacaoProcessoJudicial.NOVO,
      label: "Novo",
    },
    {
      value: TipoSituacaoProcessoJudicial.PROCURACAO_ENVIADA_PARA_SINDICO,
      label: "Procuração enviada para o sindico",
    },

    {
      value: TipoSituacaoProcessoJudicial.PROCESSO_FINALIZADO,
      label: "Processo finalizado",
    },
  ];

  const { mutateAsync: postProcessoJudicialHistorico } = usePostCriarProcessoJudicialHistorico();

  const renderBodyModalAdicionarHistoricoProcessoJudicial = () => {
    return (
      <Container>
        <Row>Registre aqui o histórico do processo.</Row>
        <Row className="mt-4">
          <Col xl={5} sm={5} xs={5} className="me-4">
            {renderCampoData()}
          </Col>
          <Col xl={5} sm={5} xs={5}>
            {renderCampoSituacao()}
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xl={5} sm={5} xs={5} className="me-4 p-0">
            {renderCheckboxPossuiAcordosVinculados()}
          </Col>

          <Col xl={5} sm={5} xs={5}>
            {renderSelecionarAcordos()}
          </Col>
        </Row>
        <Row>
          <Col xs={8} className="mt-3">
            {renderCampoAnotacoes()}
          </Col>
        </Row>
        <Row>
          <Col className="ps-0">{renderCheckboxSalvarCadastrarOutroHistorico()}</Col>
        </Row>
      </Container>
    );
  };

  const renderCampoData = () => {
    return (
      <CustomDatePicker
        key={"data-historico-processo-judicial"}
        name="data"
        label="Data *"
        usarValidacaoParaFormulario
        control={control}
        setValue={setValue}
        register={register}
      />
    );
  };

  const renderCampoSituacao = () => {
    return (
      <CustomSelect
        id="select-filtrar-situacao"
        label="Situação *"
        name="situacao"
        isClearable
        control={control}
        register={register}
        setValue={setValue}
        usarValidacaoParaFormulario
        options={opcoesSitucoesProcessoJudical}
      />
    );
  };

  const renderSelecionarAcordos = () => {
    return (
      <CustomMultiSelectAsync
        id="acordos"
        name="acordos"
        register={register}
        key={"acordos"}
        control={control}
        setValue={setValue}
        required
        endpoint={`${api.acordo.getRegistroAcordoByUnidadeId}/${props.unidadeId}`}
        nomeAtributoLabel={`numeroAcordo`}
        atributosRefresh={[toogleAtributoRefresh]}
        customNomeAtributoLabelPrefix={`Acordo `}
        nomeAtributoValue={"id"}
        tooltip="Os acordos vinculados no processo serão exibidos no histórico do processo."
        label="Acordos"
        isDisabled={!possuiAcordosVinculados}
        isClearable
        usarValidacaoParaFormulario
      />
    );
  };

  const renderCheckboxPossuiAcordosVinculados = () => {
    return (
      <div className="mt-4">
        <CheckBox
          id="cadastrar-proximo-historico-processo-judicial"
          name="possuiAcordosVinculados"
          usarValidacaoParaFormulario
          control={control}
          register={register}
          checked={possuiAcordosVinculados}
          setValue={setValue}
          onChange={(isChecked) => {
            setPossuiAcordosVinculados(!possuiAcordosVinculados);
            if (!isChecked) {
              clearErrors("acordos");
              setValue("acordos", undefined);
            }
          }}
        />
        <span>Processo possui acordos vinculados?</span>
      </div>
    );
  };

  const renderCampoAnotacoes = () => {
    return (
      <CustomTextArea
        id="anotacoes"
        usarValidacaoParaFormulario
        className="mt-4"
        label="Anotações *"
        direcaoExpansao="both"
        control={control}
        register={register}
        setValue={setValue}
        name="anotacoes"
      />
    );
  };

  const renderCheckboxSalvarCadastrarOutroHistorico = () => {
    return (
      <div className="mt-4">
        <CheckBox
          id="cadastrar-proximo"
          usarValidacaoParaFormulario={false}
          onChange={setCadastrarOutroHistorico}
          value={cadastrarOutroHistorico}
        />
        <span>Após salvar cadastrar outro histórico do processo</span>
      </div>
    );
  };

  const renderFooterModalAdicionarHistoricoProcessoJudicial = () => {
    return (
      <>
        <CustomButton id="botaoCancelar" variant="danger-alt" label="Cancelar" onClick={() => closeModal()} />
        <CustomButton
          id="botaoSalvar"
          label="Salvar"
          onClick={(e) => {
            handleSubmit(criarProcessoHistoricoJudicial)();
          }}
        />
      </>
    );
  };

  const criarProcessoHistoricoJudicial = async (form: ModalAdicionarHistoricoProcessoJudicialForm) => {
    const processoHistoricoJudicialDTO = {
      processoJudicialId: props.processoId,
      data: form.data,
      situacao: getipoSitucoesProcessoJudicalValue(
        form.situacao as unknown as SelectOptionType<TipoSituacaoProcessoJudicial>
      ),
      anotacoes: form.anotacoes,
      acordoIds: form.acordos?.map((a) => a?.value),
      isGeradoPeloSistema: false,
    } as RequestCriarProcessoJudicialHistorico;

    try {
      const response = await postProcessoJudicialHistorico(processoHistoricoJudicialDTO);

      if (response?.status === 201) {
        reset();
        setPossuiAcordosVinculados(false);
        setToogleAtributoRefresh(!toogleAtributoRefresh);
        if (!cadastrarOutroHistorico) {
          props.onCloseModal();
        }
      }
    } catch (error: any) {
      toast.error("Erro ao salvar o histórico!");
    }
  };

  const fecharModal = () => {
    setCadastrarOutroHistorico(false);
    setPossuiAcordosVinculados(false);
    reset();
    props.onCloseModal();
  };
  const closeModal = () => {
    if (formState.isDirty) {
      showAlert({
        texto: "Existem alterações que não foram salvas na tela. Tem certeza de que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => fecharModal(),
      });
    } else {
      reset();
      setCadastrarOutroHistorico(false);
      setPossuiAcordosVinculados(false);
      props.onCloseModal();
    }
  };

  return (
    <CustomModal
      key={"modal-adicionar-historico-processo-judicial"}
      titulo="Adicionar histórico do processo"
      onCloseModal={() => closeModal()}
      size="lg"
      tipoModal="MODAL_LATERAL"
      showModal={props.showModal}
      scrollable
      fecharModalAoClicarFora={!formState.isDirty}
      customFooter={renderFooterModalAdicionarHistoricoProcessoJudicial()}
    >
      {renderBodyModalAdicionarHistoricoProcessoJudicial()}
    </CustomModal>
  );
};

export default ModalAdicionarProcessoJudicialHistorico;
